import { searchPlaces } from '@/services/placesService'
import type { ActivityCategory } from '@/services/activityService'

export interface Coordinates {
  latitude: number
  longitude: number
}

export async function geocodeDestination(destination: string): Promise<Coordinates | null> {
  const results = await searchPlaces(destination)
  const first = results[0]
  return first ? { latitude: first.latitude, longitude: first.longitude } : null
}

export interface PoiSuggestion {
  id: string
  name: string
  category: ActivityCategory
  latitude: number
  longitude: number
  address?: string
}

const CATEGORY_BY_OSM_TAG: Record<string, ActivityCategory> = {
  attraction: 'discovery',
  museum: 'discovery',
  viewpoint: 'discovery',
  gallery: 'discovery',
  zoo: 'discovery',
  theme_park: 'discovery',
  park: 'discovery',
  garden: 'discovery',
  restaurant: 'meal',
  cafe: 'meal',
  bar: 'meal',
}

interface OverpassElement {
  type: string
  id: number
  lat?: number
  lon?: number
  center?: { lat: number; lon: number }
  tags?: Record<string, string>
}

// Overpass API (OpenStreetMap) — free, no API key, no request quota to manage.
export async function findNearbyActivitySuggestions(
  coordinates: Coordinates,
  radiusMeters = 5000,
): Promise<PoiSuggestion[]> {
  const { latitude, longitude } = coordinates
  const query = `
    [out:json][timeout:25];
    (
      node["tourism"~"attraction|museum|viewpoint|gallery|zoo|theme_park"](around:${radiusMeters},${latitude},${longitude});
      node["leisure"~"park|garden"](around:${radiusMeters},${latitude},${longitude});
      node["amenity"~"restaurant|cafe|bar"](around:${radiusMeters},${latitude},${longitude});
    );
    out center 40;
  `
  const response = await fetch('https://overpass-api.de/api/interpreter', {
    method: 'POST',
    body: `data=${encodeURIComponent(query)}`,
  })
  if (!response.ok) throw new Error('Overpass request failed')
  const data = (await response.json()) as { elements: OverpassElement[] }

  const seenNames = new Set<string>()
  const suggestions: PoiSuggestion[] = []
  for (const element of data.elements ?? []) {
    const name = element.tags?.name
    if (!name || seenNames.has(name)) continue
    const tagValue = element.tags?.tourism ?? element.tags?.leisure ?? element.tags?.amenity
    const category = tagValue ? CATEGORY_BY_OSM_TAG[tagValue] : undefined
    const lat = element.lat ?? element.center?.lat
    const lon = element.lon ?? element.center?.lon
    if (!category || lat === undefined || lon === undefined) continue

    seenNames.add(name)
    suggestions.push({
      id: `${element.type}/${element.id}`,
      name,
      category,
      latitude: lat,
      longitude: lon,
      address: element.tags?.['addr:street'],
    })
  }
  return suggestions.slice(0, 20)
}

export interface WikivoyageHighlight {
  title: string
  extract: string
  url: string
}

// Wikivoyage — free, no API key, CC-BY-SA (attribution required, see `url`).
export async function getWikivoyageHighlight(
  destination: string,
): Promise<WikivoyageHighlight | null> {
  const title = destination.split(',')[0]?.trim()
  if (!title) return null

  const url = `https://fr.wikivoyage.org/w/api.php?action=query&prop=extracts&exintro&explaintext&format=json&origin=*&titles=${encodeURIComponent(title)}`
  const response = await fetch(url)
  if (!response.ok) return null
  const data = (await response.json()) as {
    query?: { pages?: Record<string, { title?: string; extract?: string; missing?: string }> }
  }
  const page = Object.values(data.query?.pages ?? {})[0]
  if (!page || page.missing !== undefined || !page.extract) return null

  return {
    title: page.title ?? title,
    extract: page.extract,
    url: `https://fr.wikivoyage.org/wiki/${encodeURIComponent(page.title ?? title)}`,
  }
}
